<template>
    <Dialog v-model:visible="display" :modal="true" class="reg-dialog tech" :baseZIndex="104" :closable="false">
        <template #header>
            <Header />
        </template>
        <div class="box">
            <div class="box-content">
                <div class="main">
                    <div class="progress-wrapper" v-if="showSpinner">
                        <ProgressSpinner />
                    </div>
                    <div class="content" v-else>
                        <div style="text-align: center;">
                            <PencilIcon style="width: 60px;" class="mobile-hide" />
                            <h1 v-if="!isDirectSignUp" class="p-text-center">
                                {{ t("registration.popup.headline1") }}
                            </h1>
                            <h1 v-else class="p-text-center">
                                {{ t("registration.tech.headline2") }}
                            </h1>
                            <Timeline :step="step" />
                            <p v-if="!isDirectSignUp" class="p-mb-4">{{ t("registration.headline4") }}</p>
                        </div>
                        <div class="reg-form">
                            <div class="card-reg">
                                <div>
                                    <span class="p-float-label">
                                        <Dropdown id="salutation" data-cy="wizard-salutation"
                                                  v-model="v$.salutation.$model" :options="salutations"
                                                  :class="{'p-invalid':v$.salutation.$error && submit}" />
                                        <label class="requiredLabel" for="salutation">{{ t("salutation") }} *</label>
                                    </span>
                                    <div v-if="submit">
                                        <small class="p-error" v-for="(error, index) of v$.salutation.$errors"
                                               :key="index">
                                            {{ t(error.$message) }}
                                        </small>
                                    </div>
                                </div>
                                <div>
                                    <span class="p-float-label">
                                        <Dropdown id="title" v-model="v$.academicTitle.$model" :options="titles"
                                                  :class="{'p-invalid':v$.academicTitle.$error && submit}" />
                                        <label for="title">{{ t("academic.title") }}</label>
                                    </span>
                                    <div v-if="submit">
                                        <small class="p-error" v-for="(error, index) of v$.academicTitle.$errors"
                                               :key="index">
                                            {{ t(error.$message) }}
                                        </small>
                                    </div>
                                </div>
                                <div>
                                    <span class="p-float-label">
                                        <InputText id="firstName" data-cy="wizard-firstName" v-model="user.firstName"
                                                   type="text" :class="{'p-invalid':v$.firstName.$error && submit}" />
                                        <label class="requiredLabel"
                                               for="firstname">{{ t("profile.form.firstname") }} *</label>
                                    </span>
                                    <div v-if="submit">
                                        <small class="p-error" v-for="(error, index) of v$.firstName.$errors"
                                               :key="index">
                                            {{ t(error.$message) }}
                                        </small>
                                    </div>
                                </div>
                                <div>
                                    <span class="p-float-label">
                                        <InputText id="lastName" data-cy="wizard-lastName" v-model="v$.lastName.$model"
                                                   type="text" :class="{'p-invalid':v$.lastName.$error && submit}" />
                                        <label class="requiredLabel"
                                               for="lastname">{{ t("profile.form.lastname") }} *</label>
                                    </span>
                                    <div v-if="submit">
                                        <small class="p-error" v-for="(error, index) of v$.lastName.$errors"
                                               :key="index">
                                            {{ t(error.$message) }}
                                        </small>
                                    </div>
                                </div>
                                <div class="span-2 phone">
                                    <span class="p-float-label">
                                        <InputText type="tel"
                                                   v-model="v$.phoneNumber.$model"
                                                   :class="{ 'p-invalid': v$.phoneNumber.$error && submit }"
                                                   data-cy="wizard-phoneNumber" />
                                        <div class="country-prefix">+49</div>
                                        <label>{{t("phone") + (isDirectSignUp ? "" : " *")}}</label>
                                    </span>
                                    <div v-if="submit">
                                        <small class="p-error" v-for="(error, index) of v$.phoneNumber.$errors"
                                               :key="index">
                                            {{ t(error.$message) }}
                                        </small>
                                    </div>
                                </div>
                                <div class="span-2">
                                    <span class="p-float-label">
                                        <AutoComplete forceSelection
                                                      :autoHighlight="true"
                                                      :class="{ 'p-invalid': v$.zipCode.$error && submit }"
                                                      v-model="readableLocation"
                                                      @complete="loadLocations($event)"
                                                      :suggestions="locations"
                                                      @clear="user.zipCode=''"
                                                      @itemSelect="locationItemSelected($event)"
                                                      data-cy="wizard-zipCode" />
                                        <label>{{t("place.of.residence.postcode") + (isDirectSignUp ? "" : " *")}}</label>
                                    </span>
                                    <div v-if="submit">
                                        <small class="p-error" v-for="(error, index) of v$.zipCode.$errors"
                                               :key="index">
                                            {{ t(error.$message) }}
                                        </small>
                                    </div>
                                </div>
                                <div v-if="!isDirectSignUp" class="span-2">
                                    <span class="p-float-label ">
                                        <InputText id="emailAddress" type="username" v-model="v$.emailAddress.$model"
                                                   data-cy="reg-usermail"
                                                   :onkeypress="checkMail()"
                                                   :class="{'p-invalid':v$.emailAddress.$error && submit || showLoginInsteadOfContinue}" />
                                        <label for="username">{{ t("registration.label1") }} *</label>
                                    </span>
                                    <div v-if="submit">
                                        <small id="no-email" class="p-error"
                                               v-for="(error, index) of v$.emailAddress.$errors" :key="index">
                                            {{ t(error.$message) }}
                                        </small>
                                    </div>
                                    <div>
                                        <small id="no-email" class="p-error" v-if="showLoginInsteadOfContinue">
                                            {{ t("registration.error3") }}
                                        </small>
                                    </div>
                                </div>
                                <div class="span-2">
                                    <span class="p-float-label">
                                        <Password id="password" v-model="v$.password.$model"
                                                  autocomplete="one-time-code"
                                                  :feedback="false"
                                                  toggleMask
                                                  data-cy="reg-password"
                                                  :class="{'p-invalid':v$.password.$error && submit}">
                                            <template #header>
                                                <h3>{{ t("registration.password.choose") }}</h3>
                                                <h6>{{ t("registration.password.strength") }}</h6>
                                            </template>
                                            <template #footer="sp">
                                                {{ sp.level }}
                                                <Divider />
                                                <h6>{{ t("registration.password.hint") }}</h6>
                                                <ul class="p-pl-2 p-ml-2 p-mt-0" style="line-height: 1.5">
                                                    <li>{{ t("registration.password.hint.signs") }}</li>
                                                </ul>
                                            </template>
                                        </Password>
                                        <label for="password">{{ t("password") }} *</label>
                                    </span>
                                    <div>
                                        <small id="password-weak" class="p-error" v-if="v$.password.$model && v$.password.$model.length < 8">
                                            {{ t("registration.password.safety") }} <span> {{(8 - v$.password.$model.length)}} {{t('registration.password.charleft')}}</span>
                                        </small>
                                    </div>
                                    <small class="p-error" v-for="(error, index) of v$.password.$errors"
                                           :key="index">
                                        {{ t(error.$message) }}
                                    </small>
                                </div>
                                <div class="span-2 p-mb-4">
                                    <span class="p-float-label m-span-2">
                                        <Password id="password2"
                                                  v-model="v$.password2.$model"
                                                  :feedback="false"
                                                  toggleMask
                                                  autocomplete="one-time-code"
                                                  data-cy="reg-password-repeat"
                                                  :class="{'p-invalid':v$.password2.$error && submit || v$.password.$model && v$.password2.$model && v$.password.$model != v$.password2.$model}" />
                                        <label for="password2">{{ t("registration.password.repeat") }} *</label>
                                    </span>
                                    <small class="p-error" v-for="(error, index) of v$.password2.$errors"
                                           :key="index">
                                        {{ t(error.$message) }}
                                    </small>
                                    <small class="p-error" v-if="submit && v$.password.$model && v$.password2.$model && v$.password.$model != v$.password2.$model">
                                        {{ t('validate.password.sameas.error') }}
                                    </small>
                                </div>
                            </div>
                            <div v-if="!isDirectSignUp" style="margin-top: 10px; margin-bottom: 20px;">
                                <span class="p-float-label terms-and-conditions p-mb-4">
                                    <Checkbox id="acceptTermsAndConditions"
                                              data-cy="termsAndConditionsCheckbox"
                                              v-model="v$.acceptTermsAndConditions.$model"
                                              :binary="true"
                                              :class="{'p-invalid':v$.acceptTermsAndConditions.$error && submit}" />
                                    <label class="pointerevents no-tooltip"
                                           for="termsandconditions">
                                        {{ t("registration.data.policy.label1") }} <a href="/datenschutz"
                                                                                      target="_blank">{{ t("privacy.policy") }}</a>{{t("registration.data.policy.label2")}} *
                                    </label>
                                </span>
                                <div v-if="submit">
                                    <small class="p-error" v-for="(error, index) of v$.acceptTermsAndConditions.$errors"
                                           :key="index">
                                        {{ t(error.$message) }}
                                    </small>
                                </div>
                            </div>

                            <b class="p-error" v-if="error">{{ error }}</b>
                            <div class="call-to-action mobile-bottom-nav">

                                <Button v-if="isDirectSignUp" data-cy="button-profile-prev"
                                        class="button-secondary button-back p-pl-3"
                                        @click="prev()">
                                    <i class="pi pi-angle-left" />{{t("back")}}
                                </Button>

                                <Button v-if="!showLoginInsteadOfContinue" @click="signup"
                                        data-cy="button-profile-next"
                                        class="button-primary button-next p-pr-3">
                                    {{isDirectSignUp ? t('submit') : t('continue')}} <i class="pi pi-angle-right p-ml-1" />
                                </Button>

                                <Button v-else @click="login"
                                        data-cy="button-profile-next"
                                        class="button-primary stretch button-next p-pr-3">
                                    {{t('registration.button.continue.login')}} <i class="pi pi-angle-right" />
                                </Button>

                            </div>
                        </div>
                    </div>
                </div>
                <div class="sidebar">
                    <div class="content">
                        <div class="mobile-hide sidebar-image"><img src="/kandidaten/img/Step 0-tech.png" /></div>
                        <div class="mobile-hide headline">{{ t("registration.tech.headline1") }}</div>
                    </div>
                </div>
            </div>
        </div>

    </Dialog>
</template>

<script lang="ts" setup>
    import { computed, defineEmits, getCurrentInstance, ref, watch, defineProps } from "vue";
    import useVuelidate from '@vuelidate/core';
    import { email, helpers, required, requiredIf } from '@vuelidate/validators';
    import { useI18n } from "vue-i18n";
    import { createAuthenticationService } from '../../services/authenticationService';
    import { createCandidateService } from '@/services/candidateService';
    import {convertSalutationForDatabase, getFeatureFlagWithApp} from "@/helper";
    import { CandidateProfile } from "@/models/CandidateProfile";
    import { useRoute } from "vue-router";
    import { BrandId } from "@/models/BrandIds";
    import { createCityZipCodeService } from "@/services/cityZipCodeService";
    import { PencilIcon } from "@heroicons/vue/outline";
    import Timeline from "@/registration/tech/wizard/Timeline.vue";
    import Header from "@/components/Header.vue";
    import validator from "validator";

    const props = defineProps(["isDirectSignUp"]);
    const isDirectSignUp = ref(props.isDirectSignUp);
    const emit = defineEmits(['close', 'registered', 'login', 'prev']);
    const { t } = useI18n();
    const route = useRoute();
    const app = getCurrentInstance();
    const display = ref(false);
    const user = ref([]);
    const step = ref(0);
    const showLoginInsteadOfContinue = ref(false);
    const close = function () {
        display.value = false;
        emit("close");
    }
    const registrationData = ref<CandidateProfile>([]);

    const titles = ref([null, "Dr.", "Prof.", "Prof. Dr."]);
    const salutations = computed(() => {
        return [
            t("salutations.mr"),
            t("salutations.mrs")
        ]
    });

    const login = () => {
        emit("login", null, { email: user.value.emailAddress });
        display.value = false;
    }

    const checkMail = async () => {
        if (!user.value?.emailAddress) {
            showLoginInsteadOfContinue.value = false;
            return;
        }

        if (!validator.isEmail(user.value.emailAddress)) {
            showLoginInsteadOfContinue.value = false;
            return;
        }
        showLoginInsteadOfContinue.value = await authenticationService.emailAlreadyExists(user.value.emailAddress);
    }

    const show = function (email: string, candidateData: CandidateProfile) {
        if (!candidateData)
            user.value.emailAddress = email;
        else
            user.value = candidateData;

        if (isDirectSignUp.value)
            step.value = 3;
        showSpinner.value = false;
        error.value = undefined;
        display.value = true;
    }
    const submit = ref(false);
    const error = ref();
    const showSpinner = ref(false);
    const authenticationService = createAuthenticationService(app);
    const passwordsNotMatch = ref(false);

    const signup = async () => {
        submit.value = true;
        passwordsNotMatch.value = user.value.password2 != user.value.password;
        if (validate() || passwordsNotMatch.value)
            return;
        showSpinner.value = true;
        error.value = undefined;

        const singUpSuccess = await authenticationService.signUpWithPassword({
            brandId: BrandId.ElliotBrowne,
            emailAddress: user.value.emailAddress,
            password: user.value.password,
            encryptedCandidateId: user.value.encryptedId,
            source: route.query.source ? route.query.source?.toString() : "organic"
        });

        if (singUpSuccess) {
            initRegistrationData();
            registrationData.value.brandId = BrandId.ElliotBrowne;
            if (route.query.source) {
                registrationData.value.sourceParam = route.query.source?.toString();
            } else {
                registrationData.value.sourceParam = "organic";
            }
            registrationData.value = { ...registrationData.value, ...user.value };
            registrationData.value.salutation = convertSalutationForDatabase(registrationData.value.salutation);

            const candidateService = await createCandidateService(app);
            const candidateId = await candidateService.getId();

            if (candidateId) {
                registrationData.value.id = candidateId;
                const inserted = await candidateService.updateCandidate(registrationData.value);
                if (inserted) {
                    emit("registered");
                    const candidateCvAddProfilePageIntegrationFeatureFlag = await getFeatureFlagWithApp(app, "candidateCvAddProfilePageIntegration");
                    if (candidateCvAddProfilePageIntegrationFeatureFlag) {
                        await candidateService.updated(candidateId);
                    }
                    close();
                }
            } else {
                console.error("failed to get candidate id for registration data", registrationData);
                error.value = t("registration.error2");
            }
        } else {
            if (singUpSuccess == false) {
                error.value = t("registration.error2");
            }
            if (singUpSuccess == null) {
                error.value = t("registration.error3");
            }
        }
        showSpinner.value = false;
    }

    const initRegistrationData = () => {
        if (!registrationData.value.areasOfExpertise)
            registrationData.value.areasOfExpertise = [];
        if (!registrationData.value.skills)
            registrationData.value.skills = [];

        if (!registrationData.value.jobTitles)
            registrationData.value.jobTitles = [];

        if (!registrationData.value.additionalCities)
            registrationData.value.additionalCities = [];

        if (!registrationData.value.reachableFederalStates)
            registrationData.value.reachableFederalStates = [];

        if (!registrationData.value.desiredCompanySizes)
            registrationData.value.desiredCompanySizes = [];

        if (!registrationData.value.desiredClientSizes)
            registrationData.value.desiredClientSizes = [];

        if (!registrationData.value.excludedCompanies)
            registrationData.value.excludedCompanies = [];

        //if (!registrationData.value.willingnessToChange)
        //    registrationData.value.willingnessToChange = WillingnessToChange.VeryHigh;

        if (!registrationData.value.fullTime)
            registrationData.value.fullTime = true;
    }

    const readableLocation = ref < string > ();
    const requestNameByZip = async (zip: string) => {
        const service = await createCityZipCodeService();
        if (zip) {
            const result = await service.zip(zip);

            if (result.status == 200) {
                if (result.data) {
                    const data = result.data as any;
                    return data.zip + ", " + data.cityName;
                }
            }
        }
        return null;
    };
    const locations = ref();
    const loadLocations = async (event) => {
        if (event.query.trim().length < 3) {
            locations.value = [];
        } else {
            const service = await createCityZipCodeService();
            const data = await service.locations(event.query.trim());
            locations.value = data.data;
        }
    };
    const locationItemSelected = (event) => {
        user.value.zipCode = event.value?.split(" ")[0];
    };

    watch(
        () => user.value.zipCode,
        async (zipCode) => {
            const result = await requestNameByZip(zipCode);
            readableLocation.value = result ? result : zipCode;
        }
    );

    const rules = computed(() => {
        return {
            acceptTermsAndConditions: {
                requiredIf: helpers.withMessage(t("registration.data.policy.please.accept"), requiredIf(() => {
                    return !isDirectSignUp.value;
                }))
            },
            salutation: { required: helpers.withMessage(t("validate.required.error"), required) },
            emailAddress: {
                required: helpers.withMessage(t("validate.required.error"), required),
                email: helpers.withMessage(t("validate.email.error"), email)
            },
            firstName: { required: helpers.withMessage(t("validate.required.error"), required) },
            lastName: { required: helpers.withMessage(t("validate.required.error"), required) },
            password: {
                required: helpers.withMessage(t("validate.required.error"), required),
            },
            password2: {
                required: helpers.withMessage(t("validate.required.error"), required),
            },
            zipCode: {
                requiredIf: helpers.withMessage(t("validate.required.error"), requiredIf(() => {
                    return !props.isDirectSignUp;
                })),
            },
            phoneNumber: {
                requiredIf: helpers.withMessage(t("validate.required.error"), requiredIf(() => {
                    return !props.isDirectSignUp;
                })),
            },
            academicTitle: {},
        }
    });

    const v$ = useVuelidate(rules, user);
    const validate = () => {
        v$.value.$touch();
        if (v$.value.$invalid) {
            return v$.value.$error;
        }
    }

    const prev = () => {
        emit("prev", user.value);
        display.value = false;
    }

    defineExpose({
        show,
        close,
    });

</script>

<style>
    .p-input-icon-right > i:last-of-type {
        color: blue !important;
    }

    .phone .country-prefix {
        display: none;
    }

    .phone .p-component:focus ~ .country-prefix,
    .phone .p-filled ~ .country-prefix {
        display: block;
        position: absolute;
        left: 15px;
        top: 26px;
    }

    .phone span.p-float-label .p-inputtext.p-component {
        padding-left: 45px;
    }

</style>
